<template>
    <div class="flex flex-col gap-2">
        <div class="flex flex-col">
            <h2 class="text-xl font-extrabold m-0">Assign Groups</h2>
            <span class="text-sm"
                >You can check the
                <a :href="permissionChartLink" target="_blank" rel="noopener noreferrer" class="underline!"
                    >permission</a
                >
                chart to see what each role has access to within a Group.</span
            >
        </div>

        <div class="flex flex-col gap-2 md:flex-row">
            <div class="md:w-80">
                <vx-label>Group</vx-label>
                <select-group
                    v-model="groupModel"
                    relative-dropdown
                    size="lg"
                    placeholder=""
                    :reduce="(option) => option"
                    :disabled="props.disabled || limitReached"
                ></select-group>
            </div>
            <div class="flex grow gap-2">
                <div class="grow">
                    <vx-label>Role</vx-label>
                    <vx-selectable
                        v-model="roleModel"
                        :clearable="false"
                        relative-dropdown
                        :options="groupRolesOptions()"
                        :reduce="(option) => option.value"
                        size="lg"
                        data-test="group-role-select"
                        :disabled="props.disabled || limitReached"
                    ></vx-selectable>
                </div>
                <div class="pt-7">
                    <vx-button
                        @click="addGroupAssignment"
                        :disabled="!groupModel || !roleModel || props.disabled || limitReached"
                        data-test="group-add"
                        size="lg"
                        type="button"
                    >
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
        </div>

        <div v-if="confirmedGroups.length > 0">
            <vx-list-animated v-model="confirmedGroups" label="name" value="group_id">
                <template v-slot:item="{ item }">
                    <div
                        class="grid grid-cols-1 w-full gap-1.5 px-5 py-3 text-xs text-slate-900 lg:grid-cols-2 lg:gap-2"
                    >
                        <div class="line-clamp-3 break-all font-bold lg:font-normal">{{ item.name }}</div>
                        <div class="line-clamp-3 break-all">
                            {{ groupRolesOptions().find((r) => r.value === item.role).label }}
                        </div>
                    </div>
                </template>
            </vx-list-animated>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { VxButton, VxLabel, VxSelectable, VxListAnimated } from '@voxie/frontend-components';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { groupRolesOptions, DEFAULT_GROUP_ROLE } from '~/constants/roles';
import { useToasted } from '~/composables/useToasted';
import { permissionChartLink, restrictions } from '~/constants/groups';
import SelectGroup from '~/components/general/SelectGroup.vue';

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false,
    },
});

const assignedGroups = defineModel({
    type: Array,
    default: () => [],
});

const toasted = useToasted();

const list = ref([{ group_id: '', role: DEFAULT_GROUP_ROLE, name: '' }]);

const groupModel = computed({
    get: () => (list.value[0]?.group_id ? { value: list.value[0].group_id, label: list.value[0].name } : null),
    set: (val) => {
        list.value[0].group_id = val?.value || '';
        list.value[0].name = val?.label || '';
    },
});

const roleModel = computed({
    get: () => list.value[0]?.role || DEFAULT_GROUP_ROLE,
    set: (val) => {
        list.value[0].role = val;
    },
});

const confirmedGroups = computed({
    get: () => list.value.slice(1),
    set: (newValue) => {
        // When list is modified (e.g., items deleted), update the internal list
        list.value = [list.value[0], ...newValue];
    },
});

const limitReached = computed(() => list.value.length - 1 === restrictions.MAX_GROUPS_PER_INVITE);

watch(
    list,
    (newList) => {
        const validEntries = newList.filter((item, index) => {
            if (index === 0) {
                return item.group_id && item.role;
            }
            return true;
        });
        assignedGroups.value = validEntries;
    },
    { deep: true, immediate: true }
);

const addGroupAssignment = () => {
    const draft = list.value[0];
    if (!draft.group_id || !draft.role) {
        return;
    }

    const existingGroup = list.value.slice(1).find((g) => g.group_id === draft.group_id);
    if (existingGroup) {
        toasted.error('Group is already assigned');
        return;
    }

    list.value.push({ ...draft });

    list.value[0] = { group_id: '', role: DEFAULT_GROUP_ROLE, name: '' };
};

const reset = () => {
    list.value = [{ group_id: '', role: DEFAULT_GROUP_ROLE, name: '' }];
};

defineExpose({
    reset,
});
</script>
